import axios from "axios";
import { API_ENDPOINTS } from "../config/config";
import apiClient, { handleApiError } from "./apiClient";
import { STORAGE_KEYS } from "../constants/storage";
import { clearSessionId } from "../utils/sessionManager";

/**
 * Authentication Service
 * Handles user authentication and session management
 */

/**
 * Password Login
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{success: boolean, access_token?: string, user?: Object, message?: string}>}
 */
export const loginUser = async (email, password) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.PASSWORD_LOGIN, {
      email,
      password
    });

    const { access_token, token_type } = response.data;

    if (!access_token) {
      throw new Error("Invalid authentication response: missing access token");
    }

    return {
      success: true,
      access_token,
      token_type: token_type || "bearer",
      user: {
        email,
        name: email.split("@")[0]
      }
    };
  } catch (error) {
    return handleApiError(error, "Login failed. Please try again");
  }
};

/**
 * SSO Login
 * @param {string} email - User email from SSO provider
 * @param {string} provider - SSO provider name (default: "google")
 * @param {string} credential - IdP-issued id_token (Google credential / Microsoft idToken); verified server-side
 * @returns {Promise<{success: boolean, access_token?: string, user?: Object, message?: string}>}
 */
export const loginWithSSO = async (email, provider = "google", credential = null) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.SSO_LOGIN, {
      email,
      sso_provider: provider,
      credential
    });

    const { access_token, token_type } = response.data;

    if (!access_token) {
      throw new Error("Invalid authentication response: missing access token");
    }

    return {
      success: true,
      access_token,
      token_type: token_type || "bearer",
      user: {
        email,
        name: email.split("@")[0]
      }
    };
  } catch (error) {
    return handleApiError(error, "SSO authentication failed. Please try again");
  }
};

/**
 * Validate a password-setup (invite) token — non-consuming. Public; no auth.
 * Uses a bare axios call so the apiClient 401-redirect never fires here.
 * @returns {Promise<{success, valid?, email?, first_name?, reason?, message?}>}
 */
export const validatePasswordSetup = async (token) => {
  try {
    const res = await axios.get(API_ENDPOINTS.PASSWORD_SETUP_VALIDATE, {
      params: { token }
    });
    return { success: true, ...res.data };
  } catch (error) {
    return handleApiError(error, "We couldn't validate this link.");
  }
};

/**
 * Set the password for the user a setup token identifies, then auto-login by
 * persisting the returned access token (+ claims). Public; bare axios call.
 * @returns {Promise<{success: boolean, access_token?: string, message?: string}>}
 */
export const completePasswordSetup = async (token, password) => {
  try {
    const res = await axios.post(API_ENDPOINTS.PASSWORD_SETUP, {
      token,
      password
    });
    const { access_token } = res.data;
    if (!access_token) {
      throw new Error("Invalid response: missing access token");
    }

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
    const claims = getTokenClaims() || {};
    localStorage.setItem(
      STORAGE_KEYS.USER,
      JSON.stringify({ email: claims.email, name: claims.name })
    );
    localStorage.setItem(
      STORAGE_KEYS.USER_DETAILS,
      JSON.stringify({
        name: claims.name,
        email: claims.email,
        organization_id: claims.organization_id || null,
        role: claims.role
      })
    );
    return { success: true, access_token };
  } catch (error) {
    return handleApiError(error, "We couldn't set your password.");
  }
};

/**
 * Federated SSO login — exchange an id_token that a sibling app (a LexAI tenant
 * portal) already holds for an Orbit session. The server checks it against a
 * pinned (issuer, audience) pairing and admits it only into the one org that
 * pairing maps to; Orbit's own client ids are not in that table, so a first-party
 * token sent this way is rejected.
 *
 * Uses a bare axios call so the apiClient 401-redirect never fires here — the
 * handoff page renders its own failure state and then falls back to /login.
 * @param {string} email - advisory only; the server authenticates on the token
 * @param {string} provider - "google" | "microsoft"
 * @param {string} credential - the sibling app's id_token
 * @returns {Promise<{success: boolean, access_token?: string, message?: string}>}
 */
export const loginWithFederatedSSO = async (email, provider, credential) => {
  try {
    const res = await axios.post(API_ENDPOINTS.SSO_LOGIN, {
      email,
      sso_provider: provider,
      credential,
      federated: true
    });
    const { access_token } = res.data;
    if (!access_token) {
      throw new Error("Invalid authentication response: missing access token");
    }
    return { success: true, access_token };
  } catch (error) {
    return handleApiError(error, "We couldn't sign you in from LexAI.");
  }
};

/**
 * Persist a session from an Orbit access token, reading name/org/role out of the
 * token's own claims rather than a follow-up API call. Used by the LexAI handoff.
 * @param {string} access_token
 */
export const persistSession = (access_token) => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token);
  const claims = getTokenClaims() || {};
  localStorage.setItem(
    STORAGE_KEYS.USER,
    JSON.stringify({ email: claims.email, name: claims.name })
  );
  localStorage.setItem(
    STORAGE_KEYS.USER_DETAILS,
    JSON.stringify({
      name: claims.name,
      email: claims.email,
      organization_id: claims.organization_id || null,
      role: claims.role
    })
  );
};

/**
 * User Signup (Mock implementation)
 * @param {string} name - User's name
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<{success: boolean, access_token?: string, user?: Object, message?: string}>}
 */
export const signupUser = async (name, email, password) => {
  // Simulate API call delay
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // Get existing users
  const users = JSON.parse(localStorage.getItem("orbit_users") || "[]");

  // Check if user already exists
  if (users.find((u) => u.email === email)) {
    return {
      success: false,
      message: "User with this email already exists"
    };
  }

  // Create new user
  const newUser = {
    id: users.length + 1,
    name,
    email,
    password // In real app, this would be hashed
  };

  users.push(newUser);
  localStorage.setItem("orbit_users", JSON.stringify(users));

  return {
    success: true,
    access_token: "mock_access_token_" + Date.now(),
    user: {
      id: newUser.id,
      name: newUser.name,
      email: newUser.email
    }
  };
};

/**
 * Logout - Clear all authentication data
 */
export const logout = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.USER_DETAILS);
  // Clear session ID on logout
  clearSessionId();
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) !== null;
};

/**
 * Get current user data
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  const user = localStorage.getItem(STORAGE_KEYS.USER);
  return user ? JSON.parse(user) : null;
};

/**
 * Get detailed user information
 * @returns {Object|null}
 */
export const getUserDetails = () => {
  const details = localStorage.getItem(STORAGE_KEYS.USER_DETAILS);
  return details ? JSON.parse(details) : null;
};

/**
 * Decode the (unverified) claims from the stored JWT. UI gating only —
 * the server still enforces authorization on every request.
 * @returns {Object|null}
 */
export const getTokenClaims = () => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    const b64 = part
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(part.length + ((4 - (part.length % 4)) % 4), "=");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
};

/** Current user's role (lowercased): "admin" | "editor" | "viewer" | ... */
export const getUserRole = () => (getTokenClaims()?.role || "").toLowerCase();

/** Admin/editor can edit workflows; everyone else (e.g. viewer) is read-only. */
export const canEditWorkflows = () =>
  ["admin", "editor"].includes(getUserRole());

/**
 * Platform operator (cross-tenant admin) — drives the Admin Portal nav + route
 * guard. Orthogonal to the org `role`. UI gating only; the backend
 * require_platform_admin dependency is the real boundary.
 */
export const isPlatformAdmin = () => getTokenClaims()?.is_platform_admin === true;

/**
 * May define in-house integrations: org admins, plus platform admins, exactly
 * as the backend's require_org_admin allows. UI gating only.
 */
export const canAuthorInHouse = () => getUserRole() === "admin" || isPlatformAdmin();
